import LegalPageLayout from '../components/LegalPageLayout';

export default function TermsPage() {
  return (
    <LegalPageLayout title="Terms of Service" lastUpdated="February 7, 2026">
      <p>
        Welcome to Family Waffle. These Terms of Service ("Terms") govern your use of the Family Waffle
        mobile application and related services (collectively, the "Service") operated by Family Waffle
        ("we," "us," or "our"). By accessing or using the Service, you agree to be bound by these Terms.
        If you do not agree to these Terms, do not use the Service.
      </p>

      <h2>1. Account Eligibility</h2>
      <p>
        You must be at least 13 years of age to create an account and use the Service. If you are between
        the ages of 13 and 18, you represent that your parent or legal guardian has reviewed and agrees to
        these Terms on your behalf. We do not knowingly collect or solicit personal information from
        anyone under the age of 13. If we learn that we have collected personal information from a child
        under age 13, we will delete that information as quickly as possible.
      </p>

      <h2>2. Account Registration and Security</h2>
      <p>
        To use certain features of the Service, you must register for an account. You agree to:
      </p>
      <ul>
        <li>Provide accurate, current, and complete information during registration.</li>
        <li>Maintain the security of your password and account credentials.</li>
        <li>Promptly notify us of any unauthorized use of your account.</li>
        <li>Accept responsibility for all activities that occur under your account.</li>
      </ul>
      <p>
        During registration, you must agree to these Terms and our{' '}
        <a href="/privacy">Privacy Policy</a>. Your consent is recorded and stored as an immutable
        audit record for compliance purposes.
      </p>
      <p>
        The Service supports multi-device usage. When you change your password, all other device
        sessions are automatically signed out for security. You can view and manage active sessions
        from the app settings.
      </p>
      <p>
        We reserve the right to suspend or terminate accounts that contain inaccurate or misleading
        information.
      </p>

      <h2>3. User Responsibilities and Acceptable Use</h2>
      <p>You agree to use the Service only for lawful purposes. You shall not:</p>
      <ul>
        <li>Upload, post, or share content that is illegal, harmful, threatening, abusive, harassing,
          defamatory, vulgar, obscene, or otherwise objectionable.</li>
        <li>Impersonate any person or entity, or falsely state or misrepresent your affiliation with a
          person or entity.</li>
        <li>Use the Service to transmit spam, chain letters, or unsolicited communications.</li>
        <li>Attempt to gain unauthorized access to the Service, other user accounts, or computer systems
          or networks connected to the Service.</li>
        <li>Interfere with or disrupt the Service or servers or networks connected to the Service.</li>
        <li>Use any automated means (bots, scrapers, crawlers) to access or collect data from the Service.</li>
        <li>Upload content that infringes any patent, trademark, trade secret, copyright, or other
          proprietary right of any party.</li>
        <li>Use the Service to distribute malware, viruses, or other harmful software.</li>
      </ul>
      <p>
        You may block other users at any time. Blocked users cannot send you connection requests,
        view your content, or interact with you through the Service.
      </p>
      <p>
        We reserve the right to remove any content and suspend or terminate any account that violates
        these Terms, at our sole discretion and without prior notice.
      </p>

      <h2>4. End-to-End Encryption</h2>
      <p>
        Family Waffle uses end-to-end encryption (E2E) to protect the privacy of your photos, videos,
        voice messages, comments, and other shared content. This means:
      </p>
      <ul>
        <li>All content is encrypted on your device before it leaves your phone, using industry-standard
          authenticated encryption.</li>
        <li>Only you and the recipients you designate can decrypt and view your shared content.</li>
        <li><strong>We cannot access, read, or view your encrypted content.</strong> We do not hold
          decryption keys for user content.</li>
        <li>Your encryption keys are protected by your password. If you lose access to your password
          and all your devices, your encrypted content cannot be recovered by us or anyone else.</li>
      </ul>
      <p>
        You are responsible for maintaining access to your devices and keeping your password secure,
        as your password is the sole mechanism for recovering your encryption keys on a new device.
      </p>

      <h3>4.1 "Fresh Start" Password Reset</h3>
      <p>
        If you forget your password, Family Waffle provides a "Fresh Start" feature that allows you
        to reset your password and generate new encryption keys. <strong>Because we do not store your
        encryption keys, a Fresh Start results in the permanent loss of access to all previously
        encrypted content, including photos, videos, captions, voice messages, and comments.</strong>{' '}
        Your connections and profile information are preserved. This is an inherent consequence of
        our end-to-end encryption architecture and is not a defect in the Service.
      </p>

      <h3>4.2 Public Share Links</h3>
      <p>
        The Service allows you to generate public share links for individual posts. When you create
        a public share link, the encrypted content associated with that post is made accessible to
        anyone who has the link. <strong>Content shared via public links is decrypted for public
        viewing and is no longer protected by end-to-end encryption for those viewers.</strong>{' '}
        You can revoke a public share link at any time, after which the content will no longer be
        accessible via that link.
      </p>

      <h2>5. Subscriptions and Billing</h2>
      <p>
        Family Waffle offers both free and premium subscription plans. Premium subscriptions are managed
        through the Apple App Store.
      </p>
      <ul>
        <li><strong>Free Plan:</strong> Includes core features with up to 8 connections and limited
          storage capacity. Photos and videos are uploaded in compressed quality.</li>
        <li><strong>Premium Plan:</strong> Includes unlimited connections, expanded storage, and the
          option to upload photos and videos in their original (full) quality.</li>
        <li><strong>Billing:</strong> Subscription fees are billed through your Apple ID account on a
          recurring basis (monthly or annually, depending on your chosen plan). Payment will be charged
          at confirmation of purchase.</li>
        <li><strong>Auto-Renewal:</strong> Subscriptions automatically renew unless you cancel at least 24
          hours before the end of the current billing period. You can manage and cancel your subscription
          in your Apple ID account settings.</li>
        <li><strong>Refunds:</strong> All subscription payments are processed through the Apple App Store.
          Refund requests must be submitted to Apple in accordance with their refund policies.</li>
        <li><strong>Price Changes:</strong> We may change subscription pricing from time to time. Any
          price changes will take effect at the start of the next billing period following the date of
          the price change. Your continued use of the Service after a price change constitutes your
          agreement to pay the updated price.</li>
      </ul>

      <h2>6. Account Termination and Data Deletion</h2>
      <p>You may terminate your account at any time by:</p>
      <ul>
        <li>Using the "Delete Account" option in the app settings.</li>
        <li>Contacting us at <a href="mailto:legal@familywaffle.com">legal@familywaffle.com</a>.</li>
      </ul>
      <p>
        Account deletion is subject to a <strong>30-day grace period</strong>. During this grace period:
      </p>
      <ul>
        <li>Your account is scheduled for permanent deletion.</li>
        <li>You may cancel the deletion by signing back in to your account before the grace period
          expires.</li>
        <li>Your data remains on our servers but your account is marked as pending deletion.</li>
      </ul>
      <p>After the grace period expires, the following permanent actions occur:</p>
      <ul>
        <li>Your profile information, connections, and all account data are permanently deleted.</li>
        <li>Your encrypted content (photos, videos, voice messages, comments) and associated files
          are permanently removed from our servers.</li>
        <li>Your authentication credentials are deleted.</li>
        <li>Any active subscriptions should be cancelled separately through the Apple App Store to avoid
          continued billing.</li>
        <li>Content that was shared with other users may persist in their encrypted storage until they
          choose to delete it.</li>
      </ul>
      <p>
        We reserve the right to suspend or terminate your account at any time if we determine, in our
        sole discretion, that you have violated these Terms.
      </p>

      <h2>7. Intellectual Property and Content Ownership</h2>
      <p>
        <strong>Your Content:</strong> You retain all ownership rights to the content you create, upload,
        and share through the Service, including photos, videos, captions, voice messages, and comments.
        By using the Service, you grant us a limited, non-exclusive license to store and transmit your
        encrypted content solely for the purpose of providing the Service to you and your designated
        recipients. We do not claim any ownership of your content.
      </p>
      <p>
        <strong>Our Service:</strong> The Service, including its design, features, code, and branding
        (excluding user content), is owned by Family Waffle and is protected by copyright, trademark, and
        other intellectual property laws. You may not copy, modify, distribute, sell, or lease any part of
        the Service without our prior written consent.
      </p>
      <p>
        <strong>Copyright Complaints:</strong> If you believe that content available through the Service
        (including via public share links) infringes your copyright, please contact our designated
        copyright agent at{' '}
        <a href="mailto:legal@familywaffle.com">legal@familywaffle.com</a> with: (i) a description of
        the copyrighted work, (ii) the URL or location of the allegedly infringing content, (iii) your
        contact information, (iv) a statement of good faith belief that the use is not authorized, and
        (v) a statement under penalty of perjury that the information is accurate and you are authorized
        to act on behalf of the copyright owner. We will respond to valid takedown requests in accordance
        with the Digital Millennium Copyright Act (DMCA). We reserve the right to terminate accounts of
        repeat infringers.
      </p>

      <h2>8. Limitation of Liability</h2>
      <p>
        To the maximum extent permitted by applicable law, Family Waffle and its officers, directors,
        employees, and agents shall not be liable for any indirect, incidental, special, consequential,
        or punitive damages, including but not limited to loss of data, loss of profits, or loss of
        goodwill, arising out of or in connection with your use of the Service.
      </p>
      <p>
        Without limiting the foregoing, we shall not be liable for any loss of data resulting from the
        end-to-end encryption architecture of the Service, including but not limited to data loss
        resulting from a Fresh Start password reset, loss of encryption keys, or loss of access to all
        devices.
      </p>
      <p>
        Our total aggregate liability for all claims arising out of or relating to these Terms or the
        Service shall not exceed the amount you have paid us in the twelve (12) months preceding the
        claim.
      </p>

      <h2>9. Warranty Disclaimer</h2>
      <p>
        The Service is provided on an "AS IS" and "AS AVAILABLE" basis without warranties of any kind,
        whether express or implied. We disclaim all warranties, including but not limited to implied
        warranties of merchantability, fitness for a particular purpose, and non-infringement. We do not
        warrant that the Service will be uninterrupted, secure, or error-free, or that any defects will
        be corrected.
      </p>

      <h2>10. Indemnification</h2>
      <p>
        You agree to indemnify and hold harmless Family Waffle and its officers, directors, employees,
        and agents from and against any claims, liabilities, damages, losses, and expenses (including
        reasonable attorneys' fees) arising out of or in any way connected with your access to or use of
        the Service, your violation of these Terms, or your violation of any rights of another person or
        entity. This section applies to the extent permitted by the laws of your jurisdiction. Nothing
        in this section requires you to indemnify us for losses caused by our own negligence or breach
        of these Terms.
      </p>

      <h2>11. Dispute Resolution</h2>
      <p>
        <strong>Informal Resolution:</strong> Before filing a formal legal claim, you agree to try to
        resolve any dispute informally by contacting us at{' '}
        <a href="mailto:legal@familywaffle.com">legal@familywaffle.com</a>. We will attempt to resolve
        the dispute informally within 60 days. If the dispute is not resolved within this period, either
        party may proceed with formal dispute resolution.
      </p>
      <p>
        <strong>Binding Arbitration:</strong> Any dispute, claim, or controversy arising out of or
        relating to these Terms or the use of the Service that cannot be resolved informally shall be
        settled by binding arbitration administered by the American Arbitration Association (AAA) under
        its Consumer Arbitration Rules. The arbitration shall be conducted in the English language and
        the arbitrator's decision shall be final and binding. Judgment on the arbitration award may be
        entered in any court having jurisdiction.
      </p>
      <p>
        <strong>Class Action Waiver:</strong> You agree that any dispute resolution proceedings will be
        conducted only on an individual basis and not in a class, consolidated, or representative action.
        If for any reason a claim proceeds in court rather than in arbitration, you waive any right to a
        jury trial.
      </p>
      <p>
        <strong>Exceptions:</strong> Nothing in this section shall prevent either party from seeking
        injunctive or other equitable relief from a court for matters related to intellectual property
        rights or unauthorized access to the Service.
      </p>
      <p>
        <strong>European Users:</strong> This Section 11 (Dispute Resolution) applies only to users
        residing in the United States. If you reside in the European Economic Area, the United Kingdom,
        or any jurisdiction where mandatory arbitration of consumer disputes is prohibited by law, you
        may bring claims in the courts of your country of residence, and nothing in these Terms limits
        your rights under mandatory consumer protection laws of your jurisdiction.
      </p>

      <h2>12. Changes to These Terms</h2>
      <p>
        We may update these Terms from time to time. When we do, we will revise the "Last updated" date
        at the top of this page. We will notify you of material changes by posting a notice within the
        app or sending you a notification. Your continued use of the Service after any changes constitutes
        your acceptance of the updated Terms. When material changes are made, we may require you to
        re-accept the updated Terms before continuing to use the Service, and this re-acceptance will
        be recorded in our consent audit log.
      </p>

      <h2>13. Governing Law</h2>
      <p>
        These Terms shall be governed by and construed in accordance with the laws of the State of
        Delaware, United States, without regard to its conflict of law provisions. To the extent that
        any legal action is not subject to the arbitration provision in Section 11, such action shall
        be subject to the exclusive jurisdiction of the state and federal courts located in the State
        of Delaware.
      </p>
      <h2>14. Force Majeure</h2>
      <p>
        We shall not be liable for any failure or delay in performing our obligations under these Terms
        where such failure or delay results from causes beyond our reasonable control, including but not
        limited to natural disasters, acts of government, power failures, internet or telecommunications
        failures, cyberattacks, pandemics, or other similar events.
      </p>

      <h2>15. Apple App Store Terms</h2>
      <p>
        You acknowledge that these Terms are between you and Family Waffle, not Apple. Apple has no
        obligation to furnish any maintenance or support services with respect to the Service. To the
        maximum extent permitted by applicable law, Apple will have no warranty obligation with respect
        to the Service. Apple is not responsible for addressing any claims relating to the Service,
        including product liability claims, consumer protection claims, or intellectual property
        infringement claims. Apple, Inc. and its subsidiaries are third-party beneficiaries of these
        Terms with respect to your use of the Service on Apple platforms, and Apple will have the right
        to enforce these Terms against you.
      </p>

      <h2>16. Assignment</h2>
      <p>
        You may not assign or transfer your rights or obligations under these Terms without our prior
        written consent. We may assign our rights and obligations under these Terms to an affiliate or
        in connection with a merger, acquisition, corporate reorganization, or sale of all or
        substantially all of our assets, without your consent but with notice to you.
      </p>

      <h2>17. Severability</h2>
      <p>
        If any provision of these Terms is found to be unenforceable or invalid, that provision shall be
        limited or eliminated to the minimum extent necessary so that these Terms shall otherwise remain
        in full force and effect.
      </p>

      <h2>18. Entire Agreement</h2>
      <p>
        These Terms, together with our <a href="/privacy">Privacy Policy</a>, constitute the entire
        agreement between you and Family Waffle regarding your use of the Service and supersede all prior
        agreements and understandings.
      </p>

      <h2>19. Contact Us</h2>
      <p>
        If you have any questions about these Terms, please contact us at:
      </p>
      <p>
        <a href="mailto:legal@familywaffle.com">legal@familywaffle.com</a>
      </p>
    </LegalPageLayout>
  );
}
